import { useState } from 'react';
import { canDo } from '../stages';
import { identificationBody } from '../quickActions';
import { FormButtons, FormError, grid, stack, useQuickSubmit } from './shared';
import * as S from '../../specimens/styles';
import { intakeNav } from '../../../utils/linkedRecordNav';

// The enums identification_history takes (0000, 0008).
const RANKS = ['species', 'genus', 'family', 'unknown'];
const CONFIDENCE = ['certain', 'uncertain'];
const METHODS = ['field', 'morphology', 'spore_print', 'dna', 'ai_image'];

/*
 * Log an identification of the isolate's collection
 * (POST /collections/{id}/identifications). All four of name, rank,
 * confidence and method are required (identifications.py:9). Only a
 * collection manager or internal expert may record it as approved, and it
 * is then their own approval (identifications.py:95-111).
 *
 * An intake has no identification history of its own: the form says so and
 * links to the intake (the hub's intake drawer), where its name is edited.
 */
export default function IdentificationForm({ item, user, t, onDone, onCancel, onNavigate }) {
  const [form, setForm] = useState({
    taxon_name: item.taxon_name || '', taxonomic_rank: '', confidence: '', identification_method: '',
    expert_approval: false, notes: '',
  });
  const { busy, error, setError, send } = useQuickSubmit('hub-ident');
  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  if (!item.sample_id) {
    return (
      <div style={stack}>
        <p role="note" style={S.hint}>{t('taxonomy.intakeNote')}</p>
        {item.external_id && (
          <button type="button" style={S.ghostBtn} title={t('tip.openIntake')}
            onClick={() => onNavigate(intakeNav(item.external_id).path)}>{t('taxonomy.openIntake')}</button>
        )}
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    const body = identificationBody(form, user);
    if (!body.taxon_name || !body.taxonomic_rank || !body.confidence || !body.identification_method) {
      setError(t('quick.problems.identificationIncomplete'));
      return;
    }
    const res = await send('post', `/collections/${item.sample_id}/identifications`, body);
    if (res) onDone(t('toast.identified', { name: body.taxon_name }));
  };

  return (
    <form onSubmit={submit} style={stack} data-testid="quick-identify">
      <label style={S.label}>
        {t('quick.ident.taxon')} *
        <input value={form.taxon_name} onChange={set('taxon_name')} dir="ltr" maxLength={300} style={S.input}
          aria-required="true" title={t('tip.quick.taxon')} data-testid="quick-ident-taxon" />
      </label>
      <div style={grid}>
        <label style={S.label}>
          {t('quick.ident.rank')} *
          <select value={form.taxonomic_rank} onChange={set('taxonomic_rank')} style={S.input} aria-required="true"
            title={t('tip.quick.rank')}>
            <option value="">{t('common:selectPlaceholder')}</option>
            {RANKS.map((r) => <option key={r} value={r}>{t(`common:rank.${r}`)}</option>)}
          </select>
        </label>
        <label style={S.label}>
          {t('quick.ident.confidence')} *
          <select value={form.confidence} onChange={set('confidence')} style={S.input} aria-required="true"
            title={t('tip.quick.confidence')}>
            <option value="">{t('common:selectPlaceholder')}</option>
            {CONFIDENCE.map((c) => <option key={c} value={c}>{t(`common:confidence.${c}`)}</option>)}
          </select>
        </label>
        <label style={S.label}>
          {t('quick.ident.method')} *
          <select value={form.identification_method} onChange={set('identification_method')} style={S.input}
            aria-required="true" title={t('tip.quick.method')}>
            <option value="">{t('common:selectPlaceholder')}</option>
            {METHODS.map((m) => <option key={m} value={m}>{t(`ident.methods.${m}`)}</option>)}
          </select>
        </label>
      </div>
      {canDo(user, 'approve') && (
        <label style={{ ...S.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={form.expert_approval} onChange={set('expert_approval')}
            title={t('tip.quick.approve')} data-testid="quick-ident-approve" />
          {t('quick.ident.approve')}
        </label>
      )}
      <label style={S.label}>
        {t('quick.notes')}
        <textarea rows={2} value={form.notes} onChange={set('notes')} style={S.input} title={t('tip.quick.notes')} />
      </label>
      <FormError>{error}</FormError>
      <FormButtons busy={busy} onCancel={onCancel} t={t} saveLabel={t('quick.ident.save')} />
    </form>
  );
}
